import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, Row, Col } from 'antd';
import { ContractComparisonRow } from '..';
import { CollapseExpandIcon, CustomDivider } from '../../../custom_shared/components';
import { InputComponentRegistry } from '../../node_inputs';
import { getVisibleComparisonInputIds } from '../../misc';
import { useAppStore } from '../../stores';
import type { ComparisonInputGroup } from '../../models/contract_comparison/ComparisonNodeBaseModel';
import { InputDisabledProvider } from '../../context/InputDisabledContext';
import ContractComparisonPanelHeader from './ContractComparisonPanelHeader';
import './ContractComparisonPanelRenderer.less';
import '../ContractComparisonColumns.less';

interface Props {
    panelId: string;
    panelName: string;
    nodeId: string;
    groups: ComparisonInputGroup[];
    renderGroupHeading?: (group: ComparisonInputGroup) => React.ReactNode;
}

const ContractComparisonPanelRenderer: React.FC<Props> = ({
    panelId,
    panelName,
    nodeId,
    groups,
    renderGroupHeading
}) => {
    const { contractComparisonStore } = useAppStore();

    const getChangeSummaryLabel = React.useCallback(
        (count: number) => {
            switch (contractComparisonStore.selectedFilter) {
                case 'withoutChanges':
                    return count === 1 ? '1 field' : `${count} fields`;
                case 'added':
                    return count === 1 ? '1 added' : `${count} added`;
                case 'removed':
                    return count === 1 ? '1 removed' : `${count} removed`;
                case 'modified':
                    return count === 1 ? '1 modified' : `${count} modified`;
                case 'unsaved':
                    return count === 1 ? '1 unsaved change' : `${count} unsaved changes`;
                default:
                    return count === 0 ? 'No changes' : `${count} change${count === 1 ? '' : 's'}`;
            }
        },
        [contractComparisonStore.selectedFilter]
    );

    const InputComponents = React.useMemo(
        () => InputComponentRegistry[nodeId as keyof typeof InputComponentRegistry],
        [nodeId]
    );

    const inputOrder = React.useMemo(() => Object.keys(InputComponents), [InputComponents]);

    const visibleGroups = groups.map(group => ({
        group,
        inputIds: getVisibleComparisonInputIds(contractComparisonStore.selectedFilter, group.inputIds, group.changes)
    }));

    const changeCount = groups.reduce((count, group) => count + group.changes.totalChangeCount, 0);
    const fieldCount = visibleGroups.reduce((count, visibleGroup) => count + visibleGroup.inputIds.length, 0);
    const changeSummaryLabel = getChangeSummaryLabel(
        contractComparisonStore.selectedFilter === 'all' ? changeCount : fieldCount
    );

    if (!groups.length) {
        return null;
    }

    return (
        <Collapse bordered={false} expandIcon={CollapseExpandIcon} defaultActiveKey={[panelId]}>
            <Collapse.Panel
                key={panelId}
                className="contract-comparison-panel"
                header={<ContractComparisonPanelHeader panelName={panelName} changeSummaryLabel={changeSummaryLabel} />}
            >
                <CustomDivider type="horizontal" />

                <Row className="contract-comparison-header-row contract-comparison-columns">
                    <Col>
                        <span className="header-row-text">Previous Contract · SICS</span>
                    </Col>
                    <Col>
                        <span className="header-row-text">New Contract</span>
                    </Col>
                    <Col className="contract-comparison-status" />
                </Row>

                <CustomDivider type="horizontal" />

                {visibleGroups.map(({ group, inputIds }, groupIndex) => (
                    <React.Fragment key={group.id}>
                        {groupIndex > 0 && renderGroupHeading && <CustomDivider type="horizontal" />}

                        {renderGroupHeading?.(group)}

                        <InputDisabledProvider disabled={group.isDisabled ?? false}>
                            <div className={`contract-comparison-group-fields${group.isDisabled ? ' disabled' : ''}`}>
                                {inputIds
                                    .slice()
                                    .sort((a, b) => inputOrder.indexOf(a) - inputOrder.indexOf(b))
                                    .map((inputId, index) => {
                                        const component = InputComponents[inputId as keyof typeof InputComponents];

                                        if (!component) {
                                            console.debug('Component not found for input: ', inputId);
                                            return null;
                                        }

                                        return (
                                            <ContractComparisonRow
                                                key={inputId}
                                                changeType={
                                                    group.changes.changeRecords.find(
                                                        record => record.inputId === inputId
                                                    )?.changeType
                                                }
                                                component={component}
                                                newInput={group.getNewInputById(inputId)}
                                                previousInput={group.getPreviousInputById(inputId)}
                                                isLast={index === inputIds.length - 1}
                                            />
                                        );
                                    })}
                            </div>
                        </InputDisabledProvider>
                    </React.Fragment>
                ))}
            </Collapse.Panel>
        </Collapse>
    );
};

export default observer(ContractComparisonPanelRenderer);
